export interface NLPIntent {
  intent: string;
  confidence: number;
  entities: Record<string, any>;
  query: string;
}

export interface NLPResult {
  success: boolean;
  data?: any;
  error?: string;
  affectedRows?: number;
}

export interface INLProvider {
  interpret(query: string): Promise<NLPIntent>;
  run(query: string, options?: { dryRun?: boolean }): Promise<NLPResult>;
}

export class LocalRulesProvider implements INLProvider {
  async interpret(query: string): Promise<NLPIntent> {
    const lowQuery = query.toLowerCase().trim();

    // Simple pattern matching for demonstration
    if (lowQuery.includes('show') || lowQuery.includes('list') || lowQuery.includes('find')) {
      const entities: Record<string, any> = {};

      if (lowQuery.includes('type')) entities.entityType = 'TYPE';
      if (lowQuery.includes('code')) entities.entityType = 'CODE';
      if (lowQuery.includes('active')) entities.status = 'active';
      if (lowQuery.includes('inactive')) entities.status = 'inactive';

      // Extract type names
      const typeMatch = lowQuery.match(/\btype[s]?\s+["']?([^"'\s]+)["']?/);
      if (typeMatch) entities.typeName = typeMatch[1];
      // Also support "codes in COUNTRY" or "COUNTRY codes"
      const mCodesIn = lowQuery.match(/\bcodes?\s+(?:in|of|for)\s+["']?([^"'\s]+)["']?/);
      if (mCodesIn && !entities.typeName) entities.typeName = mCodesIn[1];
      const mTypeBeforeCodes = lowQuery.match(/\b["']?([^"'\s]+)["']?\s+codes?\b/);
      if (mTypeBeforeCodes && !entities.typeName) entities.typeName = mTypeBeforeCodes[1];

      return {
        intent: 'LIST',
        confidence: 0.8,
        entities,
        query
      };
    }

    if (lowQuery.includes('create') || lowQuery.includes('add')) {
      const entities: Record<string, any> = {};

      if (lowQuery.includes('type')) entities.entityType = 'TYPE';
      if (lowQuery.includes('code')) entities.entityType = 'CODE';

      return {
        intent: 'CREATE',
        confidence: 0.7,
        entities,
        query
      };
    }

    if (lowQuery.includes('delete') || lowQuery.includes('remove')) {
      const entities: Record<string, any> = {};

      if (lowQuery.includes('type')) entities.entityType = 'TYPE';
      if (lowQuery.includes('code')) entities.entityType = 'CODE';

      return {
        intent: 'DELETE',
        confidence: 0.7,
        entities,
        query
      };
    }

    if (lowQuery.includes('update') || lowQuery.includes('modify') || lowQuery.includes('change')) {
      const entities: Record<string, any> = {};

      if (lowQuery.includes('type')) entities.entityType = 'TYPE';
      if (lowQuery.includes('code')) entities.entityType = 'CODE';

      return {
        intent: 'UPDATE',
        confidence: 0.7,
        entities,
        query
      };
    }

    return {
      intent: 'UNKNOWN',
      confidence: 0.1,
      entities: {},
      query
    };
  }

  async run(query: string, options: { dryRun?: boolean } = {}): Promise<NLPResult> {
    const intent = await this.interpret(query);

    if (intent.intent === 'UNKNOWN') {
      return {
        success: false,
        error: 'Could not understand the query. Please try rephrasing.'
      };
    }

    if (options.dryRun) {
      return {
        success: true,
        data: `Would execute: ${intent.intent} operation with entities: ${JSON.stringify(intent.entities)}`
      };
    }

    // For now, just return a mock result
    return {
      success: true,
      data: `Executed ${intent.intent} operation`,
      affectedRows: intent.intent === 'LIST' ? 5 : 1
    };
  }
}

export const nlpProvider = new LocalRulesProvider();



export class HttpNLPSProvider implements INLProvider {

  private baseUrl = import.meta.env.VITE_AI_BASE_URL_API || "";

  constructor(private mode: 'rules' | 'openai' = 'rules') { }
  async interpret(query: string): Promise<NLPIntent> {
    // server returns both intent and result; we call with dryRun to avoid side effects
    const res = await fetch(`${this.baseUrl}/ai/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: query, dry_run: true, provider: this.mode })
    });
    if (!res.ok) throw new Error(`NLPS API error: ${res.status}`);
    const data = await res.json();
    console.log("data", data)
    return data.intent as NLPIntent;
  }
  async run(query: string, options: { dryRun?: boolean } = {}): Promise<NLPResult> {
    console.log("options", options)
    console.log("options", !!options.dryRun)
    const res = await fetch(`${this.baseUrl}/ai/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: query, dry_run: !!options.dryRun, provider: this.mode })
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `NLPS API error: ${res.status}`);
    }
    const data = await res.json();
    console.log("text", data)
    return data.result as NLPResult;
  }
}

export const httpNlpProvider = new HttpNLPSProvider('rules');
export const httpOpenAiProvider = new HttpNLPSProvider('openai');
